import type { BulkUpdateTask } from '@/generated/http-clients/v3ApiSchemas'
import { timeAgo } from '@/helpers/timeFormatter'

type BulkUpdateTaskStatus = {
  when: string
  by?: string
}

export function bulkUpdateTaskMetadata(
  task: BulkUpdateTask,
): BulkUpdateTaskStatus | undefined {
  const augmentedTask = augmentBulkUpdateTask(task)
  if (augmentedTask.isSubmitted) {
    return {
      when: timeAgo(task.submittedAt),
      by: task.submittedBy,
    }
  }

  if (augmentedTask.isApproved) {
    return {
      when: timeAgo(task.approvedAt ?? ''),
      by: task.approvedBy,
    }
  }

  if (augmentedTask.isStarted) {
    return { when: timeAgo(task.startedAt ?? '') }
  }

  if (augmentedTask.isRejected) {
    return {
      when: timeAgo(task.rejectedAt ?? ''),
      by: task.rejectedBy,
    }
  }

  if (augmentedTask.isPending) {
    return {
      when: timeAgo(task.pendingSince ?? ''),
      by: task.pendingBy,
    }
  }

  if (augmentedTask.isCompleted) {
    return {
      when: timeAgo(task.completedAt ?? ''),
    }
  }

  if (augmentedTask.isFailed) {
    return {
      when: timeAgo(task.completedAt ?? ''),
    }
  }
}

type AugmentedBulkUpdateTask = BulkUpdateTask & {
  isSubmitted: boolean
  isApproved: boolean
  isRejected: boolean
  isPending: boolean
  isStarted: boolean
  isCompleted: boolean
  isFailed: boolean
}

// Create a wrapper function to augment BulkUpdateTask instances
export function augmentBulkUpdateTask(
  task: BulkUpdateTask,
): AugmentedBulkUpdateTask {
  return {
    ...task,
    get isSubmitted() {
      return this.status === 'SUBMITTED'
    },
    get isApproved() {
      return this.status === 'APPROVED'
    },
    get isRejected() {
      return this.status === 'REJECTED'
    },
    get isPending() {
      return this.status === 'PENDING'
    },
    get isStarted() {
      return this.status === 'STARTED'
    },
    get isCompleted() {
      return this.status === 'COMPLETED'
    },
    get isFailed() {
      return this.status === 'FAILED'
    },
  }
}
